/**
 * Guidelines to be displayed underneath a scratchpad
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {staticUrl} from "@khan/urls";

type Props = {
    // The content type of the project
    userAuthoredContentType: string;
};

class ScratchpadGuidelines extends React.Component<Props> {
    renderPJSGuidelines: () => null | React.ReactElement<
        React.ComponentProps<"div">
    > = () => {
        if (this.props.userAuthoredContentType !== "pjs") {
            return null;
        }

        return (
            <div>
                <h3>{t`Using disallowed functionality`}</h3>
                <p>
                    {t`To ensure that the ProcessingJS programs you create on Khan Academy will continue working in the future, they should not use functionalities intentionally disabled for reasons of security, privacy, or user experience. Generally, that means that JS programs should not be accessing \`externals\` or the \`DOM\` in some way. Here are specific examples:`}
                </p>
                <ul className={css(styles.ul)}>
                    <li>
                        {t`LocalStorage: Programs should not attempt to get or set \`localStorage\`. Doing so will cause projects to run out of quota.`}
                    </li>
                    <li>
                        {t`Links: JS Programs should not automatically open links to other web pages, on Khan Academy or otherwise; if you want to direct learners to a relevant link please use \`println()\` with the URL instead.`}
                    </li>
                    <li>
                        {t`Sound: Programs should only create sounds using the \`playSound/getSound\` functions, with the sounds listed in the soundpicker. Programs should only play sounds in response to user interaction - i.e., inside one of the \`mouse*\` or \`key*\` event handling functions. Programs should not play sounds immediately upon load, and should not loop sounds endlessly.`}
                    </li>
                </ul>
            </div>
        );
    };

    renderWebpageGuidelines: () => null | React.ReactElement<
        React.ComponentProps<"div">
    > = () => {
        if (this.props.userAuthoredContentType !== "webpage") {
            return null;
        }

        return (
            <div>
                <h3>{t`Using disallowed functionality`}</h3>
                <p>
                    {t`To ensure that the webpages you create on Khan Academy will continue working in the future, they should not use functionalities intentionally disabled for reasons of security, privacy, or user experience.`}
                </p>
                <ul className={css(styles.ul)}>
                    <li>
                        {t`LocalStorage: Webpages should not store large amounts of data in localStorage or intentionally use the same keys as other programs.`}
                    </li>
                    <li>
                        {t`Sound: Webpages should only create sounds using the files available to ProcessingJS programs or the Web Audio API oscillator. Webpages should also only play sounds in response to user interaction. Webpages should not play sounds immediately upon load.`}
                    </li>
                </ul>
            </div>
        );
    };

    renderBookmarkletGuidelines: () => React.ReactNode = () => (
        <div>
            <h3>{t`Programs with Bookmarklets`}</h3>

            <p>
                {t`Programs with bookmarklet* code are not allowed and will be hidden. Bookmarklets can circumvent functionality that has been intentionally disabled, and thus they pose a risk to the user community.`}
            </p>

            <p>
                <em>
                    {t`*A bookmarklet is any code script that the program author asks the user to save as a browser bookmark. Clicking the bookmark with the saved code will then run the script, which has the potential to edit/delete the user’s site data or collect their personal information.`}
                </em>
            </p>
        </div>
    );

    render(): React.ReactNode {
        return (
            <div>
                <p>
                    {t`We love when people create projects that expand their understanding of computer programming -- like drawings and animations, games, scientific visualizations, or entries to contests!`}
                </p>
                <p>
                    {t`To keep the community focused on learning to code and creating interesting projects that align with Khan Academy’s mission and community guidelines, the following will help you understand what kind of projects are appropriate here on Khan Academy. Projects that don’t meet the guidelines may be hidden from the "hot" list or from public view.`}
                </p>
                <p>
                    {t`Please take some time to review the guidelines below as you create your coding masterpieces. Thank you for making our community safe, informative, and appropriate for all coding learners!`}
                </p>
                <br />
                <h3>{t`About the "Hot" list`}</h3>
                <p>
                    {t`The "hot" list is a gallery of projects that have been recently popular with the community, according to votes. You can find that list by clicking "Browse projects" on the Computer Programming homepage.`}
                </p>
                <p>
                    {t`The purpose of the "hot" list is to highlight original projects that create something really cool - like a game, graphic, or interactive simulation -- feel free to vote up a project if you’re impressed by its programming, functionality, or code!`}
                </p>
                <h3>{t`Project guidelines`}</h3>
                <p>
                    {t`Projects should reflect the strides you’ve made in your coding journey, and beginners are always welcome to flex their creative muscles here! However, there are some basic rules to keep in mind as you do this.`}
                </p>
                <p>
                    {t`If a project or discussion post falls into one of the following disallowed criteria, a Guardian may hide it from the "hot" list and/or public view entirely. Learners who persistently break these rules may also get banned from community activity. Projects that spur discussions that distract from learning about computer programming and coding may also get hidden.`}
                </p>
                <p>
                    {t`A project is considered inappropriate if it falls into one or more of the following categories:`}
                </p>
                <h4>{t`Offensive`}</h4>
                <ul className={css(styles.ul)}>
                    <li>
                        {t`Containing discriminatory or offensive language.`}
                    </li>
                    <li>
                        {t`Sexual or violent content, or any other content that could reasonably be deemed inappropriate for an educational setting.`}
                    </li>
                </ul>
                <br />
                <h4>{t`Off-topic`}</h4>
                <ul className={css(styles.ul)}>
                    <li>
                        {t`Advertisements or non-helpful links to 3rd party sites.`}
                    </li>
                    <li>
                        {t`Controversial or divisive topics including religion, politics, or personal issues/revealing details that compromise learner privacy.`}
                    </li>
                    <li>
                        {t`Social groups or clubs, or projects that generate social chats, and other posts not related to coding, in the discussion thread.`}
                    </li>
                    <li>{t`Asking others to “vote up” a project.`}</li>
                    <li>
                        {t`Garnering or receiving votes from a voting ring (groups of people who conspire to vote up each others’ projects). People who participate in voting rings will be banned from community activity.`}
                    </li>
                </ul>
                <br />
                <h4>{t`Plagiarism`}</h4>
                <p>
                    {t`You can create spin-offs of projects by clicking the "Spin-off" button under each project. Creating a spin-off gives credit to the original creator and connects the new project to the original. Any learner can create a spin-off of any project.`}
                </p>
                <p>{t`It’s considered plagiarism if:`}</p>
                <ul className={css(styles.ul)}>
                    <li>
                        {t`A user pastes someone else's work into a new project, instead of creating a spin-off.`}
                    </li>
                    <li>
                        {t`A user pastes work from multiple projects without giving credit/links to those projects in comments at the top of the project.`}
                    </li>
                </ul>
                <br />
                {this.renderBookmarkletGuidelines()}
                <br />
                {this.renderPJSGuidelines()}
                {this.renderWebpageGuidelines()}
                <p>
                    {t`Nice, you read them all! You're winning like a Winston!`}
                </p>
                <p>
                    <img
                        alt=""
                        aria-hidden={true}
                        src={staticUrl("/images/avatars/cs-winston.png")}
                    />
                </p>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    ul: {
        fontSize: 14,
        listStyle: "inherit",
        paddingLeft: 20,
    },
});

export default ScratchpadGuidelines;
